import {PolymerElement, html} from '@polymer/polymer';
import {TooltipBehavior} from '../behaviors/tooltip-behavior.js';

class KeliInputContainer extends TooltipBehavior(PolymerElement) {
  static get template() {
    return html`
<style>
  :host {
    position: relative;
    display: block;
    overflow: visible;
  }
  
  .hidden {
    display: none;
    visibility: hidden;
  }

  #labelAndInputContainer {
    position: relative;
  }

  #label {
    font-size: 1em;
    font-weight: 300;
    pointer-events: none;
    cursor: default;
    white-space: nowrap;
    overflow: hidden;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    width: 100%;
    color: var(--keli-field-label-color);
  }

  #help {
    position: absolute;
    right: 0;
    font-size: .7em;
    top: -10px;
    color: var(--keli-text-subtle);
    cursor: help;
  }

  #help:hover {
    color: var(--keli-text);
  }

  .label-is-floating #label {
    font-weight: 400;
    font-size: .7em;
    top: -10px;
  }

  .label-is-overlayed #label {
    color: var(--keli-input-field-text-color, var(--keli-field-label-color));
  }

  .label-is-floating #label.help-icon-visible {
      width: calc(100% - 14px);
  }

  .label-is-highlighted #label {
    transition: top var(--keli-transition-fast), color var(--keli-transition-fast), font-size var(--keli-transition-fast);
    color: var(--keli-field-label-color-active);
  }

  .is-invalid #label {
    color: var(--keli-invalid-label);
  }

  .is-invalid ::slotted(#input) {
    background-color: var(--keli-input-invalid-background-color) !important;
    outline: var(--keli-input-invalid-outline) !important;
  }

  .is-invalid .unfocused-line, .is-invalid .focused-line {
    border-color: var(--keli-invalid-border) !important;
  }

  .is-animating .focused-line {
    animation: lineAnimation 500ms infinite linear;
    transform: none;
    transform-origin: center center;
    border-color: var(--keli-field-border-color-animated) !important;
  }

  @keyframes lineAnimation {
    50% {
      transform: scale3d(.8, 1, 1);
    }
    100% {
      transform: scale3d(1, 1, 1);
    }
  }

  .has-icon ::slotted(#input) {
    padding-right: 20px !important;
  }

  #icon {
    display: inline-block;
    position: absolute;
    right: 0;
    top: 2px;
    cursor: pointer;
    text-align: right;
    font-size: 16px;
    color: var(--keli-field-icon-color);
  }

  #icon:hover {
    color: var(--keli-field-icon-hover-color);
  }

  #lineContainer {
    height: 2px;
    position: relative;
    transform-origin: unset;
  }

  .unfocused-line {
    position: absolute;
    top: 0;
    width: 100%;
    border-bottom: 1px solid var(--keli-field-border-color);
  }

  .focused-line {
    position: absolute;
    top: 0;
    width: 100%;
    border-bottom: 2px solid var(--keli-field-border-color-active);
    transform-origin: center center;
    transform: scale3d(0, 1, 1);
  }

  .is-highlighted .focused-line {
    transform: none;
    transition: transform var(--keli-transition-medium);
  }

  :host([disabled]) .unfocused-line {
    border-bottom-style: dashed;
  }

  #errorContainer {
    font-size: .7em;
    color: var(--keli-invalid-text);
  }

  ::slotted([slot="postscript"]) {
    font-size: .7em;
  }

  #hotkey {
    color: var(--keli-text-subtle);
  }

  #addOnContainer {
    font-size: .6em;
    position: absolute;
    width: 100%;
    text-align: right;
  }

  #addOnContainer span:not(:last-of-type) {
    margin-right: .5em;
  }

  #characterCounter {
    display: none;
    color: var(--keli-text-subtle);
  }

  .is-highlighted #characterCounter {
    display: inline;
  }

  #postscriptContainer {
    position: absolute;
    overflow: visible;
  }
</style>

<div id="labelAndInputContainer" class$="[[_computeInputContentClass(inputHasContent, alwaysFloatLabel, focused, invalid, icon, _initialized)]]">
  <label id="label" for="input" slot="label" class$="[[_computeLabelClass(label, helpText, chrome, _initialized)]]">[[label]]</label>
  <sl-icon id="help" name="help-circle-outline" class$="[[_computeHelpClass(helpText, chrome, _initialized)]]"></sl-icon>
  <slot name="input"></slot>
  <sl-icon id="icon" name="[[formattedIcon]]" on-click="handleIconClick" class$="[[_computeIconClass(formattedIcon, _initialized)]]"></sl-icon>
</div>

<div id="lineContainer" class$="[[_computeUnderlineClass(focused, invalid, animating, chrome, _initialized)]]">
  <div class="unfocused-line"></div>
  <div class="focused-line"></div>
</div>

<div id="addOnContainer" class$="[[_computeAddOnClass(focused, chrome, _initialized)]]">
  <span id="characterCounter" class$="[[_computeCharacterCounterClass(_characterCounterString, chrome, _initialized)]]">[[_characterCounterString]]</span>
  <span id="hotkey" class$="[[_computeHotkeyClass(hotkey, chrome, _initialized)]]">[[hotkey]]</span>
</div>

<div id="postscriptContainer" class$="[[_computePostscriptClass(chrome, _initialized)]]">
  <slot name="postscript" id="postscript"></slot>
  <div id="errorContainer" class$="[[_computeInvalidClass(invalid, chrome, _initialized)]]">[[errorMessage]]</div>
</div>`;
  }

  static get is() {
    return 'keli-input-container';
  }

  static get properties() {
    return {
      _initialized: {type: Boolean},
      chrome: {type: Boolean, value: true},
      label: String,
      inputHasContent: Boolean,
      formattedIcon: {type: String, computed: 'computeFormattedIcon(icon)'},
      helpText: {type: String, observer: 'helpTextChanged'},
      icon: String,
      alwaysFloatLabel: Boolean,
      disabled: {type: Boolean, reflectToAttribute: true},
      focused: Boolean,
      invalid: Boolean,
      errorMessage: String,
      hotkey: Boolean,
      animating: Boolean,
      maxLength: Number,
      currentLength: {type: Number, observer: 'currentLengthChanged'},
      _characterCounterString: String
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._initialized = true;
  }

  _computeInputContentClass(inputHasContent, alwaysFloatLabel, focused, invalid, icon) {
    const classList = [];
    if (alwaysFloatLabel || inputHasContent) {
      classList.push('label-is-floating');
    } else {
      classList.push('label-is-overlayed');
    }

    if (invalid) {
      classList.push('is-invalid');
    } else if (focused && inputHasContent) {
      classList.push('label-is-highlighted');
    }
    if (icon) {
      classList.push('has-icon');
    }
    return classList.join(' ');
  }

  _computeLabelClass(label, helpText, chrome) {
    const classList = [];
    if (!label || !chrome) {
      classList.push('hidden');
    }
    if (helpText) {
      classList.push('help-icon-visible');
    }
    return classList.join(' ');
  }

  _computeHelpClass(helpText, chrome) {
    this.classList.toggle('help-icon-visible', (helpText && chrome));

    const classList = [];
    if (!helpText || !chrome) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  _computeIconClass(formattedIcon) {
    const classList = [];
    if (!formattedIcon) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  _computeUnderlineClass(focused, invalid, animating, chrome) {
    const classList = [];
    if (focused) {
      classList.push('is-highlighted');
    }
    if (invalid) {
      classList.push('is-invalid');
    }
    if (animating) {
      classList.push('is-animating');
    }
    if (!chrome) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  _computeAddOnClass(focused, chrome) {
    const classList = [];
    if (focused) {
      classList.push('is-highlighted');
    }
    if (!chrome) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  _computeCharacterCounterClass(_characterCounterString, chrome) {
    const classList = [];
    if (!_characterCounterString || !chrome) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  _computeHotkeyClass(hotkey, chrome) {
    const classList = [];
    if (!hotkey || !chrome) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  _computePostscriptClass(chrome) {
    const classList = [];
    if (!chrome) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  _computeInvalidClass(invalid, chrome) {
    const classList = [];
    if (!invalid || !chrome) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  computeFormattedIcon(icon) {
    return KeliInterface.Styles.parseIconName(icon);
  }

  currentLengthChanged(newValue) {
    if (!this.maxLength) {
      return;
    }

    const percentage = (newValue / this.maxLength) * 100;
    if (percentage >= 70) {
      this._characterCounterString = newValue.toString() + '/' + this.maxLength.toString();
    } else {
      this._characterCounterString = '';
    }
  }

  helpTextChanged(newValue) {
    const tooltip = this.getTooltipInstance(this.$.help);
    if (tooltip) {
      if (newValue) {
        tooltip.enable();
      } else {
        tooltip.disable();
      }
    } else if (newValue) {
      this.addTooltip(this.$.help, 'helpText', {
        triggerTarget: this.$.help,
        placement: 'top-end',
        offset: [0, 0]
      });
    }
  }

  handleIconClick(event) {
    event.stopPropagation();
    this.dispatchEvent(new CustomEvent('keli-input-icon-activated', {
      bubbles: true,
      composed: true,
      detail: {originalEvent: event}
    }));
  }
}

customElements.define(KeliInputContainer.is, KeliInputContainer);
export {KeliInputContainer};
